import React, { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../utils/translations';
import { ChevronRight, Play, ArrowDown, Zap, Shield, Users } from 'lucide-react';

const Hero = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const [currentIndustry, setCurrentIndustry] = useState(0);
  const industries = Object.values(t.industries);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndustry((prev) => (prev + 1) % industries.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [industries.length]);

  const scrollToNext = () => {
    const nextSection = document.getElementById('benefits');
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="min-h-screen flex flex-col justify-center relative bg-gradient-to-br from-white via-blue-50 to-gray-50 overflow-hidden" aria-labelledby="hero-heading">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse" style={{ animationDelay: '2s' }}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-orange-300 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-pulse" style={{ animationDelay: '4s' }}></div>
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10 pt-20">
        <div className="flex flex-col lg:flex-row items-center min-h-[80vh]">
          <div className="w-full lg:w-1/2 lg:pr-12 mb-12 lg:mb-0">
            {/* Trust Badge */}
            <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium mb-6 bg-white shadow-md border border-gray-200">
              <Shield className="w-4 h-4 mr-2" style={{ color: '#115d7b' }} />
              <span className="text-gray-700">
                {language === 'en' ? 'Trusted by 500+ Companies' : 'Confiado por 500+ Empresas'}
              </span>
            </div>

            <h1 id="hero-heading" className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight">
              {t.hero.title}
            </h1>
            
            {/* Fixed height container for dynamic text to prevent layout shift */}
            <div className="text-2xl md:text-3xl text-gray-600 mb-8 h-24 md:h-20 flex items-center">
              <div className="flex flex-col md:flex-row md:items-center">
                <span className="mr-0 md:mr-3 mb-2 md:mb-0">{t.hero.subtitle}</span>
                <span 
                  className="font-bold transition-all duration-500 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent min-h-[2.5rem] flex items-center"
                  key={currentIndustry}
                >
                  {industries[currentIndustry].title}
                </span>
              </div>
            </div>

            {/* SEO-friendly hidden text for search engines */}
            <div className="sr-only">
              <h2>Software ERP México - Plataforma Integral de Gestión Empresarial</h2>
              <p>LAIMU Trantor es la solución ERP líder en México que integra CRM, TMS, MRP, HRMS y KPIs para optimizar las operaciones empresariales. Más de 500 empresas confían en nuestra plataforma para obtener ROI en 6 meses.</p>
              <ul>
                <li>ERP (Enterprise Resource Planning) - Planificación de Recursos Empresariales</li>
                <li>CRM (Customer Relationship Management) - Gestión de Relaciones con Clientes</li>
                <li>TMS (Transportation Management System) - Sistema de Gestión de Transporte</li>
                <li>MRP (Manufacturing Resource Planning) - Planificación de Recursos de Manufactura</li>
                <li>HRMS (Human Resource Management System) - Sistema de Gestión de Recursos Humanos</li>
                <li>KPIs (Key Performance Indicators) - Indicadores Clave de Rendimiento</li>
              </ul>
            </div>

            {/* Key Value Props */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
              <div className="flex items-center bg-white/80 backdrop-blur-sm p-4 rounded-lg shadow-sm">
                <Zap className="w-6 h-6 mr-3" style={{ color: '#ff8c00' }} />
                <span className="font-medium text-gray-700">
                  {language === 'en' ? 'Fast Setup' : 'Configuración Rápida'}
                </span>
              </div>
              <div className="flex items-center bg-white/80 backdrop-blur-sm p-4 rounded-lg shadow-sm">
                <Shield className="w-6 h-6 mr-3" style={{ color: '#10b981' }} />
                <span className="font-medium text-gray-700">
                  {language === 'en' ? 'Secure & Reliable' : 'Seguro y Confiable'}
                </span>
              </div>
              <div className="flex items-center bg-white/80 backdrop-blur-sm p-4 rounded-lg shadow-sm">
                <Users className="w-6 h-6 mr-3" style={{ color: '#115d7b' }} />
                <span className="font-medium text-gray-700">
                  {language === 'en' ? '24/7 Support' : 'Soporte 24/7'}
                </span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <a
                href="/demo"
                className="group px-8 py-4 text-white rounded-lg transition-all duration-300 text-center font-bold text-lg shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
                style={{ backgroundColor: '#115d7b' }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#0f5269'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#115d7b'}
              >
                <span className="flex items-center justify-center">
                  {t.cta.demo}
                  <ChevronRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </span>
              </a>
              <a
                href="/solutions"
                className="group px-8 py-4 border-2 rounded-lg hover:bg-gray-50 transition-all duration-300 text-center font-bold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                style={{ color: '#115d7b', borderColor: '#115d7b' }}
              >
                <span className="flex items-center justify-center">
                  <Play className="mr-2 h-5 w-5" />
                  {language === 'en' ? 'Explore Solutions' : 'Explorar Soluciones'}
                </span>
              </a>
            </div>
          </div>

          <div className="w-full lg:w-1/2 relative">
            <div className="relative px-8 md:px-12">
              {/* Main Dashboard Image */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl transform rotate-3 hover:rotate-0 transition-transform duration-500">
                <img
                  src="https://images.pexels.com/photos/7688336/pexels-photo-7688336.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
                  alt={language === 'en'
                    ? 'Modern warehouse with ERP inventory management system - LAIMU cloud software for manufacturing in Mexico'
                    : 'Almacén moderno con sistema ERP de gestión de inventarios - Software en la nube LAIMU para manufactura en México'}
                  className="w-full rounded-2xl"
                  width="1260"
                  height="750"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/20 via-transparent to-transparent" aria-hidden="true"></div>
                
                {/* Floating Stats Cards - Adjusted positioning */}
                <div className="absolute top-4 left-4 bg-white rounded-lg shadow-lg p-3 md:p-4 animate-bounce" style={{ animationDelay: '1s', animationDuration: '3s' }}>
                  <div className="text-xl md:text-2xl font-bold" style={{ color: '#115d7b' }}>98%</div>
                  <div className="text-xs text-gray-600">{language === 'en' ? 'Success Rate' : 'Tasa de Éxito'}</div>
                </div>
                
                <div className="absolute bottom-4 right-4 bg-white rounded-lg shadow-lg p-3 md:p-4 animate-bounce" style={{ animationDelay: '2s', animationDuration: '3s' }}>
                  <div className="text-xl md:text-2xl font-bold" style={{ color: '#ff8c00' }}>500+</div>
                  <div className="text-xs text-gray-600">{language === 'en' ? 'Companies' : 'Empresas'}</div>
                </div>
              </div>

              {/* Background Decorative Elements */}
              <div className="absolute -inset-4 bg-gradient-to-r from-blue-400 to-purple-500 rounded-2xl filter blur-3xl opacity-20 -z-10"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
        <button
          onClick={scrollToNext}
          className="flex flex-col items-center text-gray-600 hover:text-gray-900 transition-colors group"
        >
          <span className="text-sm font-medium mb-2">
            {language === 'en' ? 'Discover More' : 'Descubre Más'}
          </span>
          <ArrowDown className="w-6 h-6 animate-bounce group-hover:translate-y-1 transition-transform" />
        </button>
      </div>
    </section>
  );
};

export default Hero;